<template>
  <div
    class="safe-insets relative box-border flex h-full min-h-0 w-full flex-col overflow-hidden transition-colors duration-500 ease-in-out"
    :class="isAmbientPhase ? 'bg-hazel-400' : 'bg-hazel-50'"
  >
    <Transition name="cs-fade">
      <div
        v-if="isReconnecting"
        class="pointer-events-none absolute right-4 top-4 z-50 flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-sm text-gray-600 shadow-sm"
      >
        <Spinner class="h-4 w-4" />
        <span>{{ t('reconnecting') }}</span>
      </div>
    </Transition>

    <div class="relative grid min-h-0 flex-1 grid-cols-1 grid-rows-1">
      <Transition name="cs-fade">
        <div
          v-if="displayPhase === 'dark-idle'"
          key="idle"
          class="col-start-1 row-start-1 h-full min-h-0 w-full overflow-hidden"
        >
          <DeviceIdleScreen />
        </div>
      </Transition>

      <Transition name="cs-fade">
        <div
          v-if="displayPhase === 'intro'"
          key="intro"
          class="col-start-1 row-start-1 flex h-full min-h-0 w-full items-center justify-center overflow-hidden p-8"
        >
          <Transition name="cs-fade" mode="out-in">
            <p v-if="showWelcomeText" key="welcome" class="text-4xl font-semibold">
              {{ welcomeMessage }}
            </p>
          </Transition>
        </div>
      </Transition>

      <Transition name="cs-fade">
        <CounterSignatureHandoverLayout
          v-if="displayPhase === 'active' && session"
          key="active"
          class="col-start-1 row-start-1 h-full min-h-0 w-full overflow-hidden"
          :session="session"
          :counter-id="counterId!"
          @session-updated="refetchSession"
        />
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DeviceAuthRole, type JwtDeviceDto } from '@carvia/ros-client-types';
import { useTokenPayload } from '@/queries/auth/use-login';
import {
  useCounterProcessSession,
  useCounterProcessSessionEvents,
} from '@/queries/use-counter-process-session';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLanguage, Local } from '@/entities/auth/local.enum';
import { useTemporaryLocale } from '@/hooks/use-temporary-locale';
import DeviceIdleScreen from './DeviceIdleScreen.vue';
import CounterSignatureHandoverLayout from './counter-signature/CounterSignatureHandoverLayout.vue';
import Spinner from '@/components/icons/Spinner.vue';

type DisplayPhase = 'dark-idle' | 'intro' | 'active';

const PAGE_BG_CLASS = 'counter-signature-page';
const PAGE_BG_AMBIENT_CLASS = 'counter-signature-page-ambient';

function setPageBackground(active: boolean) {
  for (const el of [document.documentElement, document.body, document.getElementById('app')]) {
    el?.classList.toggle(PAGE_BG_CLASS, active);
    if (!active) {
      el?.classList.remove(PAGE_BG_AMBIENT_CLASS);
    }
  }
}

function setPageBackgroundAmbient(ambient: boolean) {
  for (const el of [document.documentElement, document.body, document.getElementById('app')]) {
    el?.classList.toggle(PAGE_BG_AMBIENT_CLASS, ambient);
  }
}

onMounted(() => setPageBackground(true));
onUnmounted(() => setPageBackground(false));

const FADE_MS = 500;
const WELCOME_HOLD_MS = 1500;

const { t } = useI18n();
const { data: tokenPayload } = useTokenPayload();

const counterId = computed(() => {
  const payload = tokenPayload.value;
  if (!payload || !('deviceRole' in payload)) return undefined;
  const device = payload as JwtDeviceDto;
  if (device.deviceRole !== DeviceAuthRole.COUNTER_SIGNATURE_IPAD) return undefined;
  return device.counterId;
});

const { data: session, refetch: refetchSession } = useCounterProcessSession(counterId);
const { isReconnecting } = useCounterProcessSessionEvents(
  counterId,
  computed(() => !!counterId.value),
);

const displayPhase = ref<DisplayPhase>('dark-idle');
const isAmbientPhase = computed(
  () => displayPhase.value === 'dark-idle' || displayPhase.value === 'intro',
);

watch(isAmbientPhase, (ambient) => setPageBackgroundAmbient(ambient), { immediate: true });

// Child screens have their own <i18n> blocks and follow i18n.global.locale.
useTemporaryLocale(() => {
  const displayLocale = session.value?.bookingSummary?.displayLocale;
  return displayLocale ? getLanguage(displayLocale as Local) : undefined;
});

const showWelcomeText = ref(false);
let lastIntroCompletedSessionId: string | null = null;
let introRunId = 0;

const welcomeMessage = computed(() => {
  const name = [
    session.value?.bookingSummary?.customerFirstName,
    session.value?.bookingSummary?.customerLastName,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  return name ? t('welcome', { name }) : t('welcomeGeneric');
});

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function playIntro(sessionId: string) {
  const runId = ++introRunId;
  displayPhase.value = 'intro';
  showWelcomeText.value = false;
  await delay(FADE_MS);
  if (runId !== introRunId) return;

  showWelcomeText.value = true;
  await delay(FADE_MS);
  if (runId !== introRunId) return;

  await delay(WELCOME_HOLD_MS);
  if (runId !== introRunId) return;

  showWelcomeText.value = false;
  await delay(FADE_MS);
  if (runId !== introRunId) return;

  lastIntroCompletedSessionId = sessionId;
  displayPhase.value = 'active';
}

watch(
  session,
  (currentSession, previousSession) => {
    if (!currentSession) {
      introRunId += 1;
      showWelcomeText.value = false;
      displayPhase.value = 'dark-idle';
      return;
    }

    const sessionId = currentSession.sessionId;
    const previousSessionId = previousSession?.sessionId;

    if (previousSessionId !== undefined && previousSessionId !== sessionId) {
      introRunId += 1;
      showWelcomeText.value = false;
      lastIntroCompletedSessionId = null;
    }

    if (lastIntroCompletedSessionId === sessionId) {
      displayPhase.value = 'active';
      return;
    }

    void playIntro(sessionId);
  },
  { immediate: true },
);
</script>

<style scoped>
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition:
    opacity 500ms ease,
    transform 500ms ease;
}

.cs-fade-enter-from,
.cs-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>

<style>
html.counter-signature-page {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

body.counter-signature-page,
#app.counter-signature-page {
  height: 100%;
  overflow: hidden;
  @apply bg-hazel-50;
  transition: background-color 500ms ease-in-out;
}

body.counter-signature-page-ambient,
#app.counter-signature-page-ambient {
  @apply bg-hazel-400;
}
</style>

<i18n lang="json">
{
  "en": {
    "welcome": "Welcome {name}",
    "welcomeGeneric": "Welcome",
    "reconnecting": "Reconnecting…"
  },
  "de": {
    "welcome": "Willkommen {name}",
    "welcomeGeneric": "Willkommen",
    "reconnecting": "Verbindung wird wiederhergestellt…"
  },
  "es": {
    "welcome": "Bienvenido/a {name}",
    "welcomeGeneric": "Bienvenido/a",
    "reconnecting": "Reconectando…"
  }
}
</i18n>
